import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import "../styles/DetailedTrainingView.css";

Chart.register(annotationPlugin);

interface DetailedResult {
  patientType: string;
  meanRate: number;
  goodRate: number;
  averageDepth: number;
  depthHistory: number[];
  rateHistory: number[];
}

interface Props {
  details: {
    trainingResult: DetailedResult;
    sensorUsed: boolean;
  };
}

const DetailedTrainingView: React.FC<Props> = ({ details }) => {
  const { t } = useTranslation();
  const depthChartRef = useRef<HTMLCanvasElement>(null);
  const rateChartRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let depthChart: Chart | null = null;
    let rateChart: Chart | null = null;

    let targetLineValue: number;
    let boldTicks: number[];
    let yMax: number;

    switch (details.trainingResult.patientType.toLowerCase()) {
      case "child male":
      case "child female":
        targetLineValue = 4;
        boldTicks = [4];
        yMax = 7;
        break;
      case "baby male":
      case "baby female":
        targetLineValue = 3;
        boldTicks = [3];
        yMax = 7;
        break;
      case "adult male":
      case "adult female":
      default:
        targetLineValue = 5.5;
        boldTicks = [5, 6];
        yMax = 7;
        break;
    }

    if (depthChartRef.current && details.trainingResult.depthHistory) {
      const depthCtx = depthChartRef.current.getContext("2d");
      if (depthCtx) {
        depthChart = new Chart(depthCtx, {
          type: "bar",
          data: {
            labels: details.trainingResult.depthHistory.map((_, i) => i + 1),
            datasets: [
              {
                label: t("Depth (cm)"),
                data: details.trainingResult.depthHistory,
                backgroundColor: "#c3d1e3",
                base: 0.05,
                barPercentage: 1.05,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            layout: {
              padding: { top: 0, right: 10, bottom: 10, left: 0 },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                displayColors: false,
                callbacks: {
                  title: function (tooltipItems) {
                    if (tooltipItems.length > 0) {
                      const item = tooltipItems[0];
                      return `${t("Minutes")}: ${item.label}`;
                    }
                    return "";
                  },
                  label: function (context) {
                    let label = context.dataset.label || "";
                    if (label) {
                      label += ": ";
                    }
                    if (context.parsed.y !== null) {
                      label += context.parsed.y.toFixed(1);
                    }
                    return label;
                  },
                },
              },
              annotation: {
                annotations: {
                  targetLine: {
                    type: "line",
                    yMin: targetLineValue,
                    yMax: targetLineValue,
                    borderColor: "black",
                    borderWidth: 1.5,
                    borderDash: [6, 6],
                  },
                },
              },
            },
            scales: {
              x: {
                position: "top",
                title: {
                  display: true,
                  text: t("Minutes"),
                  align: "center",
                  color: "black",
                  padding: { top: 0, bottom: 10 },
                  font: {
                    weight: "normal",
                    size: 15,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
                grid: {
                  display: false,
                },
                border: {
                  display: true,
                  color: "black",
                  width: 1.5,
                },
                ticks: {
                  color: "black",
                  padding: -5,
                  font: {
                    weight: "normal",
                    size: 12,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
              },
              y: {
                reverse: true,
                min: 0,
                max: yMax,
                title: {
                  display: true,
                  text: t("Depth (cm)"),
                  padding: { top: 5, bottom: 10 },
                  color: "black",
                  font: {
                    weight: "normal",
                    size: 15,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
                grid: {
                  display: false,
                },
                ticks: {
                  stepSize: 1,
                  padding: 0,
                  color: "black",
                  font: function (context) {
                    const baseFont = {
                      size: 12,
                      family: "VirtualLabQuickSandMedium",
                    };

                    if (context && context.tick) {
                      if (boldTicks.includes(context.tick.value)) {
                        return {
                          ...baseFont,
                          family: "VirtualLabQuickSandBold",
                        };
                      }
                    }
                    return baseFont;
                  },
                },
                border: {
                  display: true,
                  color: "black",
                  width: 1.5,
                },
              },
            },
          },
        });
      }
    }

    if (rateChartRef.current && details.trainingResult.rateHistory) {
      const rateCtx = rateChartRef.current.getContext("2d");
      if (rateCtx) {
        rateChart = new Chart(rateCtx, {
          type: "line",
          data: {
            labels: ["", ...details.trainingResult.rateHistory.map((_, i) => i + 1)],
            datasets: [
              {
                label: t("Rate"),
                data: [null, ...details.trainingResult.rateHistory],
                fill: false,
                borderColor: "#a9cbe8",
                borderWidth: 2.5,
                tension: 0.2,
                pointRadius: 5,
                pointBackgroundColor: "#a9cbe8",
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            layout: {
              padding: { top: 10, right: 10, bottom: 0, left: 5 },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                displayColors: false,
                callbacks: {
                  title: function (tooltipItems) {
                    if (tooltipItems.length > 0) {
                      const item = tooltipItems[0];
                      if (item.label) {
                        return `${t("Minutes")}: ${item.label}`;
                      }
                    }
                    return "";
                  },
                  label: function (context) {
                    let label = context.dataset.label || "";
                    if (label) {
                      label += ": ";
                    }
                    if (context.parsed.y !== null) {
                      label += context.parsed.y;
                    }
                    return label;
                  },
                },
              },
              annotation: {
                annotations: {
                  topTargetLine: {
                    type: "line",
                    yMin: 120,
                    yMax: 120,
                    borderColor: "black",
                    borderWidth: 1.5,
                    borderDash: [6, 6],
                  },
                  bottomTargetLine: {
                    type: "line",
                    yMin: 100,
                    yMax: 100,
                    borderColor: "black",
                    borderWidth: 1.5,
                    borderDash: [6, 6],
                  },
                },
              },
            },
            scales: {
              x: {
                position: "bottom",
                title: {
                  display: true,
                  text: t("Minutes"),
                  align: "center",
                  color: "black",
                  padding: { top: 5, bottom: 0 },
                  font: {
                    weight: "normal",
                    size: 15,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
                grid: {
                  display: false,
                },
                border: {
                  display: true,
                  color: "black",
                  width: 1.5,
                },
                ticks: {
                  color: "black",
                  padding: -2,
                  font: {
                    weight: "normal",
                    size: 12,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
              },
              y: {
                min: 0,
                max: 180,
                title: {
                  display: true,
                  text: t("Rate"),
                  padding: { top: 0, bottom: 5 },
                  color: "black",
                  font: {
                    weight: "normal",
                    size: 15,
                    family: "VirtualLabQuickSandMedium",
                  },
                },
                grid: {
                  display: false,
                },
                ticks: {
                  stepSize: 20,
                  padding: 0,
                  color: "black",
                  font: function (context) {
                    const baseFont = {
                      size: 12,
                      family: "VirtualLabQuickSandMedium",
                    };

                    if (context && context.tick) {
                      if (
                        context.tick.value === 100 ||
                        context.tick.value === 120
                      ) {
                        return {
                          ...baseFont,
                          family: "VirtualLabQuickSandBold",
                        };
                      }
                    }
                    return baseFont;
                  },
                },
                border: {
                  display: true,
                  color: "black",
                  width: 1.5,
                },
              },
            },
          },
        });
      }
    }

    return () => {
      depthChart?.destroy();
      rateChart?.destroy();
    };
  }, [details, t]);

  const InfoCard: React.FC<{
    title: string;
    value: string | number;
    unit?: string;
    icon: string;
  }> = ({ title, value, unit, icon }) => (
    <div className="info-card">
      <div className="info-card-top">
        <img src={`/images/${icon}`} alt={title} className="info-card-icon" />
      </div>

      <div className="info-card-bottom">
        <div className="info-card-title">{title}</div>
        <div className="info-card-value">
          {value} {unit && <span className="info-card-unit">{unit}</span>}
        </div>
      </div>
    </div>
  );

  const getPatientIcon = (type: string): string => {
    switch (type.toLowerCase()) {
      case "adult male":
      case "adult female":
        return "results/Icon_Patient_Adult.png";
      case "child male":
      case "child female":
        return "results/Icon_Patient_Child.png";
      case "baby male":
      case "baby female":
        return "results/Icon_Patient_Baby.png";
      default:
        return "results/Icon_Patient.png";
    }
  };

  return (
    <div className="detailed-view-container">
      <div className="section-header details-header">
        {t("Training details")}
      </div>
      <div className="details-content">
        <div className="info-grid">
          <InfoCard
            title={t("Patient")}
            value={t(details.trainingResult.patientType)}
            icon={getPatientIcon(details.trainingResult.patientType)}
          />
          <InfoCard
            title={t("Mean rate")}
            value={details.trainingResult.meanRate}
            unit="/min"
            icon="results/Icon_MeanRate.png"
          />
          <InfoCard
            title={t("Good rate")}
            value={details.trainingResult.goodRate}
            unit="%"
            icon="results/Icon_GoodRate.png"
          />
          {details.sensorUsed &&
            <InfoCard
              title={t("Average depth")}
              value={details.trainingResult.averageDepth || "0"}
              icon="results/Icon_NumberOfCompressions.png"
            />
          }
        </div>
        <div className="charts-grid">
          {details.sensorUsed &&
            <div className="chart-container">
              <canvas ref={depthChartRef}></canvas>
            </div>
          }
          <div className="chart-container">
            <canvas ref={rateChartRef}></canvas>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DetailedTrainingView);
